<template>
  <div
    class="relative mb-8"
    :data-ab-id="dataAbId"
  >
    <SfScrollable
      :active-index="activeIndex"
      class="pointer-events-auto relative mx-4 w-full snap-x snap-mandatory flex-col [-ms-overflow-style:none] [scrollbar-width:none] desktop:mx-0 desktop:w-auto desktop:flex-row [&::-webkit-scrollbar]:hidden"
      direction="horizontal"
      buttons-placement="floating"
      is-active-index-centered
      :prev-disabled="false"
      :next-disabled="false"
      @on-scroll="updateActiveIndex"
      @on-prev="
        ({ preventDefault }) => {
          preventDefault();
          setActiveIndex((activeIndex > 1 ? activeIndex - 1 : carouselItems.length - 2));
        }
      "
      @on-next="
        ({ preventDefault }) => {
          preventDefault();
          setActiveIndex(activeIndex > carouselItems.length - 3 ? 1 : (activeIndex + 1) % carouselItems.length);
        }
      "
    >
      <template #previousButton="defaultProps">
        <KikButton
          v-bind="defaultProps"
          :disabled="false"
          class="absolute left-0 z-[2] ml-3 hidden !rounded-full desktop:flex"
          variant="secondary"
          size="sm"
          square
        >
          <SfIconChevronLeft size="sm" />
        </KikButton>
      </template>
      <template #nextButton="defaultProps">
        <KikButton
          v-bind="defaultProps"
          :disabled="false"
          class="absolute right-0 z-[2] mr-3 hidden !rounded-full desktop:flex"
          variant="secondary"
          size="sm"
          square
        >
          <SfIconChevronRight size="sm" />
        </KikButton>
      </template>
      <div
        v-for="(item, index) in carouselItems"
        :key="index"
        class="relative m-0 flex shrink-0 grow snap-center flex-col justify-end overflow-hidden lg:w-[330px] xl:w-[468px]"
        @mouseenter="stopAutoplay"
        @mouseleave="startAutoplay"
        @touchstart="stopAutoplay"
        @touchend="startAutoplay"
      >
        <SharedMyKikBadge
          v-if="item.myKikBadge"
          :badge="item.myKikBadge[0]"
        />
        <video
          v-if="item.asset.type === 'media'"
          preload="metadata"
          class="aspect-[1/1.1966] w-full object-cover"
          :muted="item.asset.muted"
          :autoplay="item.asset.enableAutoplay"
          controls
          :controlsList="item.asset.controlsList"
          :disablePictureInPicture="item.asset.disablePicInPic"
          :playsinline="item.asset.inlineOnly"
        >
          <source
            :src="item.asset.src"
            type="video/mp4"
          >
        </video>
        <SharedLink
          v-if="item.asset.type === 'image'"
          :link="item.asset?.link"
          class="relative size-full"
        >
          <NuxtImg
            v-if="item.asset.type === 'image'"
            :src="item.asset.src"
            :alt="item.asset.alt"
            sizes="lg:100vw 48vw"
            :srcset="item.asset.srcset"
            :width="item.asset.dimensions.width"
            :height="item.asset.dimensions.height"
            :loading="imageLoadType"
            :preload="imageLoadType === 'eager'"
            :fetchpriority="imageLoadType === 'eager' ? 'high' : 'low'"
            class="aspect-[1/1.1966] w-full object-cover"
          />
        </SharedLink>
        <div
          class="absolute flex w-full flex-col justify-end px-4 pb-[30px] desktop:mb-[6px]"
          :class="{ 'background-filter': item.asset.type === 'image',
                    '!mb-16': item.asset.type === 'media' }"
          :style="{ alignItems: isMobile ? left : `${item.contentAlignment}` }"
        >
          <div
            class="flex items-center gap-1 mobile:items-start"
            :style="{ color: `${item.titleColor}`, textAlign: (isMobile ? 'left' : item.contentAlignment) }"
          >
            <h2 class="z-[1] mb-1 text-[24px] font-bold leading-[28px] desktop:text-[28px] desktop:leading-[32px]">
              {{ item.title }}<span
                v-if="item.enableLegalDisclaimer && item.titleOrSubtitle === 'title'"
                class="z-[1] -mt-1 ml-1 inline-flex cursor-pointer align-middle"
                tabindex="0"
                @click="openLegal(item)"
                @keydown="(e: KeyboardEvent) => keyHandler(e, [], () => isLegalOpen = true)"
              ><SfIconInfo
                class="translate-y-[2px]"
                size="base"
              /></span>
            </h2>
          </div>
          <div
            class="z-[1] flex items-center gap-1 text-xl"
            :style="{ color: `${item.subtitleColor}` }"
          >
            <span>
              {{ item.subTitle }}
              <span
                v-if="item.enableLegalDisclaimer && item.titleOrSubtitle === 'subtitle'"
                class="-mt-1 ml-1 inline-flex cursor-pointer align-middle"
                tabindex="0"
                @click="openLegal(item)"
                @keydown="(e: KeyboardEvent) => keyHandler(e, [], () => isLegalOpen = true)"
              ><SfIconInfo
                class="translate-y-[2px]"
                size="base"
              /></span>
            </span>
          </div>
          <div
            v-if="item.links.length"
            class="z-[1] mt-3 flex flex-wrap gap-2"
          >
            <SharedLink
              v-for="(link, i) in item.links"
              :key="`link-${i}`"
              :link="link.link"
              class="rounded-md border px-4 py-2 text-center mobile:!text-base"
              :style="{
                color: `${link.ctaTextColor}`,
                backgroundColor: `${link.buttonColor}`,
                borderColor: `${link.borderColor}`,
              }"
            >
              {{ link.ctaText }}
            </SharedLink>
          </div>
        </div>
        <KikLegalInformationDrawer
          :legal-text="currentLegalText"
          :title="currentLegalTitle"
          :open-button="false"
          :is-open="isLegalOpen"
          @on-visibility-change="isLegalOpen = $event"
        />
      </div>
    </SfScrollable>
    <div class="absolute bottom-[-1] hidden w-full gap-1 bg-white pt-0.5 desktop:flex">
      <p
        v-for="({}, i) in items"
        :key="`dot-${i}`"
        class="h-1 w-full bg-neutral-200"
        :class="{
          '!bg-primary-700': activeIndex === i || activeIndex === i + 1 || activeIndex === i - 1,
        }"
        @click="setActiveIndex(i)"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { SfIconInfo, SfIconChevronLeft, SfIconChevronRight, SfScrollable, type SfScrollableOnScrollData } from '@storefront-ui/vue';
import type { itemsStruct } from './TileDisplay.model';
import magnoliaGetters from '~/composables/getters/magnoliaGetters';
import keyHandler from '~/helpers/a11y/keyHandler';

interface Props {
  autoplay?: boolean;
  autoplayTime?: string;
  desktopDisplayMode?: string;
  items?: any[];
  imageLoadType?: HTMLImageElement['loading'];
  dataAbId?: string;
}

const props = withDefaults(defineProps<Props>(), {
  autoplay: false,
  autoplayTime: '5000',
  items: () => [],
  imageLoadType: 'lazy',
});

const { autoplay, autoplayTime, items } = toRefs(props);
const { $domain } = useNuxtApp();
const { isMobile } = useDevice();
const activeIndex = ref(1);
const interval = ref();
const isLegalOpen = ref(false);
const currentLegalText = ref('');
const currentLegalTitle = ref('');

const stopAutoplay = () => {
  clearInterval(interval.value);
};

const carouselItems = computed(() => {
  return items.value?.map((item: itemsStruct) => {
    let asset = {};
    if (item.assetMode?.field === 'image') {
      asset = {
        type: item.assetMode.field,
        src: magnoliaGetters.getDefaultSrcAsset(item.assetMode.image, $domain.cmsAssetDomainPath),
        srcset: magnoliaGetters.getSrcSetImagesAsString(item.assetMode.image, $domain.cmsAssetDomainPath, item.assetMode.aspectRatio, item.assetMode.mobileAspectRatio?.[0]?.aspectRatio, item.assetMode.mobileImage?.[0]?.image),
        link: item.assetMode.imageLink?.[0] ? magnoliaGetters.getLinkAndName(item.assetMode.imageLink[0], $domain.cmsAssetDomainPath, item.assetMode.imageLink[0]?.switchable?.intLinkParam) : {},
        title: magnoliaGetters.getTitleImage(item.assetMode.image, item.assetMode.imageTitle || ''),
        alt: magnoliaGetters.getAltImage(item.assetMode.image, item.assetMode.alt || ''),
        dimensions: magnoliaGetters.getImageDimensions(item.assetMode.image),
      };
    }
    if (item.assetMode?.field === 'media') {
      const deviceMedia = isMobile && item.assetMode.mobileMedia?.[0] ? item.assetMode.mobileMedia[0].media : item.assetMode.media;
      asset = {
        type: item.assetMode.field,
        src: `${magnoliaGetters.getDefaultSrcAsset(deviceMedia, $domain.cmsAssetDomainPath)}#t=0.2`,
        muted: item.assetMode.muted,
        controlsList: magnoliaGetters.getControlsList(item.assetMode),
        disablePicInPic: item.assetMode.disablePicInPic,
        inlineOnly: item.assetMode.inlineOnly,
        enableAutoplay: item.assetMode.enableAutoplay,
      };
    }

    const links = item.ctaLink?.length
      ? item.ctaLink.map(cta => ({
          ctaText: cta.ctaText,
          ctaTextColor: magnoliaGetters.getHexColor(cta.ctaTextColor),
          buttonColor: magnoliaGetters.getHexColor(cta.buttonColor),
          borderColor: magnoliaGetters.getHexColor(cta.borderColor),
          link: magnoliaGetters.getLinkAndName(cta, $domain.cmsAssetDomainPath, cta.switchable?.intLinkParam),
        }))
      : [];
    return {
      ...item,
      titleColor: magnoliaGetters.getHexColor(item.titleColor),
      subtitleColor: magnoliaGetters.getHexColor(item.subtitleColor),
      links,
      asset,
    };
  });
});

const isClickEvent = ref(false);

const openLegal = (item: any) => {
  currentLegalText.value = item.legalText;
  currentLegalTitle.value = item.legalTitle;
  isLegalOpen.value = true;
};

watch(() => isLegalOpen.value, () => {
  if (isLegalOpen.value) {
    stopAutoplay();

    return;
  }

  startAutoplay();
});

const setActiveIndex = (index: number) => {
  isClickEvent.value = true;
  if (index < 1 || index > carouselItems.value?.length) {
    return;
  }
  activeIndex.value = index;
  setTimeout(() => {
    isClickEvent.value = false;
  }, 500);
  startAutoplay();
};

const updateActiveIndex = (event: SfScrollableOnScrollData) => {
  if (isClickEvent.value) {
    return;
  }
  const itemWidth = event.scrollWidth / carouselItems.value.length;
  const newActiveIndex = Math.round(event.left / itemWidth) + 1;
  activeIndex.value = newActiveIndex < 1 ? 1 : newActiveIndex;
};

const startAutoplay = () => {
  if (autoplay.value) {
    clearInterval(interval.value);
    interval.value = setInterval(() => {
      let nextIndex = activeIndex.value + 1;
      if (nextIndex >= carouselItems.value.length - 1) {
        nextIndex = 1;
      }
      setActiveIndex(nextIndex);
    }, (Number(autoplayTime.value) * 1000));
  }
};

onMounted(() => {
  startAutoplay();
});
onUnmounted(() => {
  clearInterval(interval.value);
});
</script>
